import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query'
import {} from 'types/contribution'
import {
  generateContributionReport as generateContributionReportMethod,
  fetchContributionReport,
  fetchContributionReports,
  deleteContributionReport as deleteContributionReportMethod,
} from '../methods'
import {
  ContributionReportGenerateRequestDto,
  ContributionReportGenerateResponseDto,
  ContributionReportResponseManyDto,
  ContributionReportResponseOneDto,
} from '../dtos'

export enum ContributionReportQueryKeys {
  GENERATE = 'contribution-report-generate',
  GET = 'contribution-report-get',
  GET_ALL = 'contribution-reports-get-all',
  DELETE = 'contribution-report-delete',
}

export function useContributionReport() {
  const generateContributionReport = (options?: {
    mutationOptions?: UseMutationOptions<
      ContributionReportGenerateResponseDto,
      Error,
      ContributionReportGenerateRequestDto
    >
  }) =>
    useMutation({
      mutationKey: [ContributionReportQueryKeys.GENERATE],
      mutationFn: (data) => generateContributionReportMethod(data),
      ...options?.mutationOptions,
      onSuccess: (data, variables, context) => {
        if (options?.mutationOptions?.onSuccess) {
          options.mutationOptions.onSuccess(data, variables, context)
        }
      },
    })

  const getContributionReport = (
    jobId: string,
    options?: {
      queryOptions?: UseQueryOptions<ContributionReportResponseOneDto, Error>
    }
  ) =>
    useQuery<ContributionReportResponseOneDto, Error>({
      queryKey: [ContributionReportQueryKeys.GET, jobId],
      queryFn: () => fetchContributionReport(jobId as string),
      refetchInterval: 30000,
      ...options?.queryOptions,
      enabled: !!jobId && options?.queryOptions?.enabled,
    })

  const getContributionReports = (
    clientPublicKey: string,
    options?: {
      queryOptions?: UseQueryOptions<ContributionReportResponseManyDto, Error>
    }
  ) =>
    useQuery<ContributionReportResponseManyDto, Error>({
      queryKey: [ContributionReportQueryKeys.GET_ALL, clientPublicKey],
      queryFn: () => fetchContributionReports(clientPublicKey),
      refetchOnWindowFocus: false,
      enabled: options?.queryOptions?.enabled !== false && !!clientPublicKey,
      gcTime: 30 * 60 * 1000, // Cache company for 30 minutes since it doesn't change often
      ...options?.queryOptions,
    })

  const deleteContributionReport = (options?: {
    mutationOptions?: UseMutationOptions<void, Error, string>
  }) =>
    useMutation({
      mutationKey: [ContributionReportQueryKeys.DELETE],
      mutationFn: (jobId: string) => deleteContributionReportMethod(jobId),
      ...options?.mutationOptions,
      onSuccess: (data, variables, context) => {
        if (options?.mutationOptions?.onSuccess) {
          options.mutationOptions.onSuccess(data, variables, context)
        }
      },
    })

  return {
    generateContributionReport,
    getContributionReport,
    getContributionReports,
    deleteContributionReport,
  }
}
